import { useCallback } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import {
  ConsumptionTypeDto,
  ShowUsageCardConsumptionsPerEanResponse,
  ShowUsageCardResponse,
} from '@eneco/dc-be/client';
import { useFormatter } from '@eneco/i18n';
import { IconButton, Stack, Text } from '@eneco/sparky';
import { ChevronRightIcon, ElectricityIcon, GasIcon, RedeliveryIcon } from '@eneco/sparky/icons';
import { styled } from '@eneco/sparky/stitches';
import { useContent } from '@sitecore/common';
import { DashboardOverviewRendering, Fields } from '@sitecore/types/DashboardOverview';

import { StyledDashboardBlock } from './DashboardBlock';
import { DashboardLink } from './DashboardLink';

export const ShowUsageCard = ({
  card,
  addressIdentifier,
}: {
  card: ShowUsageCardResponse;
  addressIdentifier: string;
}) => {
  const { fields } = useContent<DashboardOverviewRendering>();
  const { format, date } = useFormatter();
  if (card.hasAnalogMeter) {
    return (
      <StyledDashboardBlock>
        <Text>
          <RichText html={fields.showUsageCard?.analogueMeterText?.value} />
        </Text>
        <DashboardLink
          href={`${fields.showUsageCard?.addMeterReadingLink?.value?.href}?consumptionType=Total&address=${addressIdentifier}`}
          emphasis="high"
          trackingComponentName="show_usage_add_meter_reading_link"
          trackingComponentType="dashboard_link">
          {fields.showUsageCard?.addMeterReadingLink?.value?.text}
        </DashboardLink>
      </StyledDashboardBlock>
    );
  }

  const details = card.details;

  if (details?.consumptions.length === 0) {
    return (
      <StyledDashboardBlock>
        <Text>
          <RichText html={fields.showUsageCard?.newCustomerText?.value} />
        </Text>
        <DashboardLink
          href={`${fields.showUsageCard?.addMeterReadingLink?.value?.href}?consumptionType=Total&address=${addressIdentifier}`}
          emphasis="high"
          trackingComponentName="show_usage_add_meter_reading_link"
          trackingComponentType="dashboard_link">
          {fields.showUsageCard?.addMeterReadingLink?.value?.text}
        </DashboardLink>
      </StyledDashboardBlock>
    );
  }

  const startDate = details?.consumptions[0]?.startDate;
  const endDate = details?.consumptions[0]?.endDate;

  return (
    <StyledDashboardBlock>
      <Stack gap={4} direction="column" alignY={'center'}>
        {startDate !== undefined && endDate !== undefined && (
          <Text size={'BodyS'} color="textLowEmphasis">
            {format(fields.showUsageCard?.dateRangeText?.value, {
              startDate: date.short(startDate || ''),
              endDate: date.short(endDate || ''),
            })}
          </Text>
        )}
        <Stack gap={2} direction="column">
          {details?.consumptions.map(consumption => (
            <UsageCard data={consumption} fields={fields} />
          ))}
        </Stack>
      </Stack>
      <DashboardLink
        href={`${fields?.showUsageCard?.seeUsageLink?.value?.href}?consumptionType=Total&address=${addressIdentifier}`}
        emphasis="high"
        trackingComponentName="show_usage_see_usage_link"
        trackingComponentType="dashboard_link">
        {fields?.showUsageCard?.seeUsageLink?.value?.text}
      </DashboardLink>
    </StyledDashboardBlock>
  );
};

const StyledEnergyContainer = styled('div', {
  width: '100%',
});

const StyledEnergyCard = styled('div', {
  backgroundColor: '#F3F3F3',
  borderRadius: '8px',
  padding: '10px',
  paddingLeft: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '68px',
});

const StyledCardLeft = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const StyledIconBox = styled('div', {
  width: '56px',
  height: '36px',
  borderRadius: '8px',
  backgroundColor: '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '28px',
});

const UsageCard = ({ data, fields }: { data: ShowUsageCardConsumptionsPerEanResponse; fields: Fields }) => {
  const icon =
    data.consumptionType === 'Electricity' ? (
      <ElectricityIcon color={'iconElectricity'} />
    ) : data.consumptionType === 'Gas' ? (
      <GasIcon color={'iconGas'} />
    ) : (
      <RedeliveryIcon color={'iconSolar'} />
    );

  const mapConsumptionType = useCallback(
    (consumptionType: ConsumptionTypeDto) => {
      switch (consumptionType) {
        case 'Electricity':
          return { label: fields.showUsageCard?.electricityLabel?.value, unit: 'kWh' };
        case 'Gas':
          return { label: fields.showUsageCard?.gasLabel?.value, unit: 'm³' };
        case 'Injection':
          return { label: fields.showUsageCard?.injectionLabel?.value, unit: 'kWh' };
        default:
          return { label: '', unit: '' };
      }
    },
    [fields.showUsageCard],
  );

  const { label, unit } = mapConsumptionType(data.consumptionType);

  const { number } = useFormatter();

  return (
    <StyledEnergyContainer>
      <StyledEnergyCard>
        <StyledCardLeft>
          <StyledIconBox>{icon}</StyledIconBox>
          <Stack direction={'column'}>
            <Text size={'BodyXS'} weight={'bold'}>
              {label}
            </Text>
            <Text size={'BodyM'} weight={'bold'}>
              {number.noFractionDigits(data.value)} {unit}
            </Text>
          </Stack>
        </StyledCardLeft>
        <IconButton
          color="primary"
          size="regular"
          label="details"
          href={fields.showUsageCard?.seeUsageLink?.value?.href + data?.consumptionType}>
          <ChevronRightIcon />
        </IconButton>
      </StyledEnergyCard>
    </StyledEnergyContainer>
  );
};
